import { useRef, useState } from "react";
import { styled } from "styled-components";

import { ErrorMessage, HiddenImageUpload, UIButton } from "common/components";
import { UnsplashGallery } from "common/components/unsplash/UnsplashGallery";
import { fetchUnsplashAPI } from "common/components/unsplash/utils";
import { logger } from "common/utilities/logger";
import { useDispatch } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { setMapImage } from "domains/createMapDetail/actions";
import { interpolate } from "domains/language/interpolate";
import { deactivateModal } from "domains/modal/actions";
import { ModalHeader } from "domains/modal/components/ModalHeader";

const StyledTitle = styled.p`
  color: ${({ theme }) => theme.colors.textBright};
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.textBright};
`;

const UNSPLASH_URL = "https://unsplash.com/?utm_source=strategic_intelligence&utm_medium=referral";

export const MapImagePickerModal = () => {
  const translate = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState(false);
  const dispatch = useDispatch();

  const onError = () => setError(true);

  const onLocalImageUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const dispatchSetMapImage = (image: string) => {
    dispatch(setMapImage(image));
    dispatch(deactivateModal());
  };

  // Request the image download link from Unsplash
  // https://help.unsplash.com/api-guidelines/more-on-each-guideline/guideline-triggering-a-download
  const handleImageClick = async (image: string) =>
    new Promise<void>((resolve) => {
      setError(false);

      fetchUnsplashAPI(image)
        .then((json) => {
          const img = new Image();
          img.setAttribute("crossOrigin", "anonymous");
          img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            if (ctx === null) return;
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL("image/png");
            dispatchSetMapImage(dataURL);
            resolve();
          };
          img.onerror = (error) => {
            logger.error("[MapImagePickerModal] Error occurred while loading image", error);
            resolve();
          };
          // Restrict the width of the Unsplash image to 600px
          // https://unsplash.com/documentation#supported-parameters
          img.src = `${json.url}&w=600`;
        })
        .catch((error: unknown) => {
          logger.error("[MapImagePickerModal] Error occurred while fetching unplash image", error);
          setError(true);
          resolve();
        });
    });

  const Unsplash = (value: string) => (
    <StyledLink href={UNSPLASH_URL} target="_blank">
      {value}
    </StyledLink>
  );

  return (
    <>
      <ModalHeader title={translate("app.create.change_image")} />
      <StyledTitle>{translate("app.create.create_map.add_image")}</StyledTitle>
      <HiddenImageUpload accept="image/*" onSetImage={({ url }) => dispatchSetMapImage(url)} ref={inputRef} />
      <UIButton onClick={onLocalImageUploadClick} variant="filled" fullwidth>
        {translate("app.profile.upload_image.title")}
      </UIButton>
      <StyledTitle>
        {interpolate(translate("app.create.create_map.choose_image"), {
          Unsplash,
        })}
      </StyledTitle>
      {error && <ErrorMessage isActive>{translate("app.profile.upload_image.error")}</ErrorMessage>}
      <UnsplashGallery onImageClick={handleImageClick} onError={onError} />
    </>
  );
};
